import React, { useState, useEffect } from "react";
import axios from "axios";
import { SelectCustom } from "../componentes/selectComponent";
import { TablaCompleta } from "../tablas/tabla_simple";
import { Form, Space, Row, Col, Button, Upload, message, Modal, Table } from "antd";
import { FaRegEdit, FaRegTrashAlt } from "react-icons/fa";
import { InfoCircleOutlined } from "@ant-design/icons";
import { manejarError } from "../manejo_errores";
import moment from "moment";
import { HeadersAuth } from "../funciones_utiles";

export const ResultadosPorCampeonatoCategoria = ({ sesion, setAlerta }) => {
  const manejarAlertas = (tipo, mensaje) => {
    // Usar setAlerta para mostrar la alerta
    setAlerta({ message: mensaje, type: tipo });
  };
  const [campeonatos, setCampeonatos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [idCampeonatoSeleccionado, setIdCampeonatoSeleccionado] =
    useState(null);
  const [resultadosPorCategoria, setResultadosPorCategoria] = useState({});
  const [detalleModalVisible, setDetalleModalVisible] = useState(false);
  const [pilotoDetalleSeleccionado, setPilotoDetalleSeleccionado] = useState(null);

  useEffect(() => {
    fetchCampeonatos(sesion.id_asociacion);
  }, [sesion.id_asociacion]);

  const fetchCampeonatos = async (id_asociacion) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_DOMINIO}/campeonato/asociacion/${id_asociacion}`,
        HeadersAuth
      );
      // console.log(response.data)
      const campeonatosConLabel = response.data.map((c) => ({
        ...c,
        label: `${c.nombre_campeonato} (${c.año})`,
      }));
      setCampeonatos(campeonatosConLabel);
      const ultimoCampeonato = response.data.reduce((prev, curr) =>
        prev.año > curr.año || (prev.año === curr.año && prev.id > curr.id) ? prev : curr
      );
      // Establecer el campeonato seleccionado
      setIdCampeonatoSeleccionado(ultimoCampeonato.id);
    } catch (error) {
      console.error("Error al obtener campeonatos:", error);
      manejarAlertas("error", "Error al obtener campeonatos.");
    }
  };

  const fetchCategoriasPorCampeonato = async (id_campeonato) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_DOMINIO}/categorias-por-campeonato/${id_campeonato}`,
        HeadersAuth
      );
      setCategorias(response.data);
    } catch (error) {
      console.error("Error al obtener categorías:", error);
      manejarAlertas("error", "Error al obtener categorías");

    }
  };

  const fetchResultadosPorCategoria = async (id_campeonato, id_categoria) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_DOMINIO}/resultados/filtrar`,
        {
          params: { id_campeonato, id_categoria },
          ...HeadersAuth,
        }
      );
      return response.data;
    } catch (error) {
      manejarAlertas("error", "Error al obtener resultados");

      console.error("Error al obtener resultados:", error);
      return [];
    }
  };
  // console.log(resultadosPorCategoria);

  const handleCampeonatoChange = async (idCampeonato) => {
    setIdCampeonatoSeleccionado(idCampeonato);
    setCategorias([]);
    setResultadosPorCategoria({});
  
    if (idCampeonato) {
      await fetchCategoriasPorCampeonato(idCampeonato);
    }
  };
  
  useEffect(() => {
    if (idCampeonatoSeleccionado && categorias.length > 0) {
      cargarResultadosParaTodasCategorias(idCampeonatoSeleccionado);
    }
  }, [categorias]); // Se ejecuta cuando 'categorias' cambia

  const cargarResultadosParaTodasCategorias = async (idCampeonato) => {
    const resultadosTemp = {};

    for (const categoria of categorias) {
      const resultados = await fetchResultadosPorCategoria(idCampeonato, categoria.id);

      const hayPuntos = resultados.some((r) => (r.total_puntos || 0) > 0);

      resultados.sort((a, b) => {
        const puntosA = a.total_puntos || 0;
        const puntosB = b.total_puntos || 0;

        const numA = Number(a.numero_piloto ?? 0);
        const numB = Number(b.numero_piloto ?? 0);

        if (hayPuntos) {
          // 1) puntos desc
          if (puntosB !== puntosA) return puntosB - puntosA;
          // 2) desempate por numero asc
          return numA - numB;
        }

        // si no hay puntos: numero asc
        return numA - numB;
      });

      resultados.forEach((piloto, index) => {
        piloto.orden = index + 1;
      });

      resultadosTemp[categoria.id] = resultados;
    }

    setResultadosPorCategoria(resultadosTemp);
  };

  useEffect(() => {
    if (idCampeonatoSeleccionado) {
      handleCampeonatoChange(idCampeonatoSeleccionado);
    }
  }, [idCampeonatoSeleccionado]);

  const abrirDetalleModal = (piloto, carrerasConDetalle) => {
    setPilotoDetalleSeleccionado({
      nombre: `${piloto.nombre_piloto} ${piloto.apellido_piloto}`,
      carreras: [...carrerasConDetalle].sort(
        (a, b) => new Date(a.dia) - new Date(b.dia)
      ),
    });
    setDetalleModalVisible(true);
  };

  const cerrarDetalleModal = () => {
    setDetalleModalVisible(false);
    setPilotoDetalleSeleccionado(null);
  };

  const columns = [
    {
      title: "POS.",
      dataIndex: "orden", 
      key: "orden",
      align: "center",
      width: "70px",
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.orden - b.orden,
      render: (orden) => (
        <div style={{ fontSize: "14px", padding: "2px 4px" }}>
          {orden != null ? String(orden) : "-"}
        </div>
      ),
    },
    {
      title: "NRO.",
      key: "numero_piloto", 
      width: "70px",
      align: "center",
      sorter: (a, b) => a.numero_piloto.localeCompare(b.numero_piloto),
      render: (text) => (
        <div style={{ fontSize: "12px", padding: "2px 4px" }}>
          {text != null ? String(text.numero_piloto) : "-"}
        </div>
      ),
    },
    {
      title: "PILOTO",
      key: "piloto",
      align: "left",
      render: (record) => (
        <div>
          {record.nombre_piloto} {record.apellido_piloto}
        </div>
      ),
    },
    {
      title: "TOTAL DE PUNTOS",
      key: "total_puntos",
      align: "center",
      sorter: (a, b) => b.total_puntos - a.total_puntos,
      render: (a) => (
        <div style={{ fontSize: "12px", padding: "2px 4px" }}>
          {String(a.total_puntos) || 0}
        </div>
      ),
    },
    {
      title: "DESGLOSE",
      key: "desglose",
      align: "center",
      width: "90px",
      render: (record) => {
        const carrerasConDetalle = (record.carreras || []).filter(
          (carrera) => carrera.puntosDetalle && carrera.puntosDetalle.length > 0
        );

        if (carrerasConDetalle.length === 0) return "";

        return (
          <InfoCircleOutlined
            title="Ver desglose de puntos"
            style={{ color: "#1677ff", fontSize: 16, cursor: "pointer" }}
            onClick={() => abrirDetalleModal(record, carrerasConDetalle)}
          />
        );
      },
    },
  ];

  return (
    <>
      <section
        id="seccionPiloto"
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          marginTop: "1em",
        }}
      >
        <h1 className="titulos">Resultados Campeonato</h1>
      </section>
      {campeonatos.length > 0 ? (
        <>
          <section
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "1200px",
            margin: "0 auto 30px",
            display: "flex",
            justifyContent: "center"
          }}
        >
            <Row gutter={16} align="middle" justify={"center"}>
            <Col>
              <SelectCustom
                arrayOpciones={campeonatos}
                required={true}
                nombreCampo={"Seleccioná un compeonato"}
                elPlaceholder={"Seleccioná un compeonato"}
                onChange={(e) => handleCampeonatoChange(e.target.value)}
                textoOpciones={"label"}
                elValue={idCampeonatoSeleccionado || ""}
              />
            </Col>
          </Row>
        </div>
      </section>

      {categorias.length > 0 ? (
        <>
          {idCampeonatoSeleccionado && categorias.length > 0 && (
          <div style={{ padding: "10px" }}>
            {/* Agrupar categorías en filas de 2 columnas */}
            <Row gutter={[16, 16]}>
              {categorias
                .filter(
                  (categoria) => resultadosPorCategoria[categoria.id]?.length > 0
                )
                .map((categoria) => (
                  <Col
                    key={categoria.id}
                    xs={24}
                    sm={12}
                 
                  >
                    <div
                      style={{
                        border: "1px solid #e8e8e8",
                        borderRadius: "8px",
                        padding: "1em",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        minHeight: "400px",
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <h3 style={{  flexShrink: 0, color:"#BA1721" }}>
                         {categoria.categoria} ({resultadosPorCategoria[categoria.id]?.length})
                      </h3>
                    
                        <TablaCompleta
                          fetchDatos={resultadosPorCategoria[categoria.id]}
                          columns={columns}
                          elScroll={{ x: 0, y: 300 }}
                          tituloPDF={`Resultados_${categoria.categoria}`}
                          rowKey={"id_piloto"}
                          filtrarUltimaColumna={false}
                          tituloBanner={"Resultados"}
                          mostrarFiltrador={true}
                          idAsociacion={sesion.id_asociacion}
                          idCategoria={categoria.id}
                        />
                     
                    </div>
                  </Col>
                ))}
            </Row>
            {/* Mensaje si no hay categorías con resultados */}
            {categorias.length > 0 &&
              categorias.every(
                (c) => resultadosPorCategoria[c.id]?.length === 0
              ) && (
                <p style={{ textAlign: "center", marginTop: "20px" }}>
                  No hay categorías con resultados disponibles.
                </p>
              )}
          </div>
        )}
        </>
        
      ) : (
        <p style={{ textAlign: "center", marginTop: "20px", fontWeight: "lighter" }}>No hay resultados disponibles de este campeonato</p>
      )}
        </>
      ) : (
        <p style={{ textAlign: "center", marginTop: "20px", fontWeight: "lighter" }}>No hay campeonatos seleccionables.</p>
      )}

      <Modal
        title={pilotoDetalleSeleccionado?.nombre || "Desglose de puntos"}
        open={detalleModalVisible}
        onCancel={cerrarDetalleModal}
        footer={[
          <Button className="buttonModal" key="cerrar" onClick={cerrarDetalleModal}>
            Cerrar
          </Button>,
        ]}
        width={400}
      >
        {pilotoDetalleSeleccionado?.carreras.map((carrera) => (
          <div key={carrera.id_carrera} style={{ marginBottom: "1em" }}>
            <h4 style={{ marginBottom: "0.5em" }}>
              {moment(carrera.dia).format("DD/MM/YYYY")}
            </h4>
            <Table
              dataSource={carrera.puntosDetalle}
              rowKey="id"
              pagination={false}
              size="small"
              columns={[
                {
                  title: "TIPO",
                  key: "tipo",
                  render: (record) => record.tipoLabel || record.tipo,
                },
                { title: "PUNTOS", dataIndex: "puntos", key: "puntos", align: "center" },
              ]}
            />
          </div>
        ))}
      </Modal>
    </>
  );
};
